import { useMemo, useState } from 'react';

import { usePagination } from '../../../common/hooks/usePagination';
import { useTableSort } from '../../../common/hooks/useTableSort';
import {
  useCancelConsumptionReturn,
  useConsumptionReturns,
  useConsumptionReturnSources,
  useSaveConsumptionReturn,
} from '../../../hooks/useConsumptionReturns';
import { usePermissions } from '../../../hooks/usePermissions';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';
import { sortRows } from '../../../utils/sortRows';
import { useMaterialScopeOptions } from '../../hooks/useMaterialScopeOptions';
import {
  INITIAL_RETURN_FORM,
  RETURN_STATUSES,
  RETURNS_PER_PAGE,
  RETURN_TEXTS as TEXTS,
} from '../constants/consumptionReturnTexts';
import { buildReturnDocumentForm } from '../utils/buildReturnDocumentForm';
import { buildReturnItem } from '../utils/buildReturnItem';
import { buildReturnPayload } from '../utils/buildReturnPayload';
import { canSaveReturnForm } from '../utils/canSaveReturnForm';
import { filterReturns } from '../utils/filterReturns';

/**
 * Hook de la pestaña Devoluciones de obra: listado, autorización técnica, recepción de
 * sobrantes y anulación de pendientes. Lo llama la página y entrega props con nombre.
 * @param {Object} [options]
 * @param {boolean} [options.enabled] Consulta el listado solo con la pestaña activa.
 * @returns {Object} Props del panel, `drawerProps` del drawer y `resetSort`.
 */
export const useConsumptionReturnsPanel = ({ enabled = true } = {}) => {
  const scope = useMaterialScopeOptions();
  const { data: warehouses = [] } = useWarehouses();
  const { permissions } = usePermissions();
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const { page, setPage, rowsPerPage, handleRowsPerPageChange } = usePagination(RETURNS_PER_PAGE);
  const [form, setForm] = useState(null);
  const [sourceId, setSourceId] = useState('');
  const [legacyArticleId, setLegacyArticleId] = useState('');
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [returnToCancel, setReturnToCancel] = useState(null);
  const query = useConsumptionReturns(filters, { enabled });
  const sources = useConsumptionReturnSources(form?.uuid_project);
  const mutation = useSaveConsumptionReturn();
  const cancelMutation = useCancelConsumptionReturn();
  const tableSort = useTableSort(() => setPage(1));

  const rows = useMemo(
    () => sortRows(filterReturns(query.data || [], search), tableSort.sort),
    [query.data, search, tableSort.sort]
  );

  const openCreate = () => {
    setError('');
    setSourceId('');
    setLegacyArticleId('');
    setForm({ ...INITIAL_RETURN_FORM, request_id: crypto.randomUUID() });
  };

  const openDocument = (row) => {
    setError('');
    setForm(buildReturnDocumentForm(row));
  };

  const change = (name, value) =>
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'uuid_project' ? { items: [] } : {}),
      request_id: crypto.randomUUID(),
    }));

  const add = () => {
    const source = form.legacy
      ? scope.articles.find((row) => row.uuid === legacyArticleId)
      : sources.data?.find((row) => row.uuid === sourceId);
    if (!source) return;
    const item = buildReturnItem(source, form.legacy);
    if (form.items.some((row) => row.key === item.key)) return;
    change('items', [...form.items, item]);
  };

  const changeQuantity = (key, value) =>
    change(
      'items',
      form.items.map((row) => (row.key === key ? { ...row, quantity: value } : row))
    );

  const save = async () => {
    setError('');
    try {
      await mutation.mutateAsync({ id: form.uuid, payload: buildReturnPayload(form) });
      setForm(null);
    } catch (failure) {
      setError(getApiErrorMessage(failure, TEXTS.error));
    }
  };

  const confirmCancel = async () => {
    setActionError('');
    try {
      await cancelMutation.mutateAsync(returnToCancel.uuid);
    } catch (failure) {
      setActionError(getApiErrorMessage(failure, TEXTS.cancelError));
    } finally {
      setReturnToCancel(null);
    }
  };

  const changeFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value || undefined }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearch('');
    setPage(1);
  };

  const canReceive = permissions.canCheckExternalOrderStock;

  return {
    actionError,
    canAuthorize: permissions.canManageInternalOrders,
    cancelTarget: returnToCancel,
    filters,
    isCancelling: cancelMutation.isPending,
    isLoading: query.isLoading,
    loadError: query.error ? getApiErrorMessage(query.error, TEXTS.loadError) : '',
    onCancelClose: () => setReturnToCancel(null),
    onCancelConfirm: confirmCancel,
    onCancelRequest: setReturnToCancel,
    onClear: clearFilters,
    onClearActionError: () => setActionError(''),
    onCreate: openCreate,
    onFilter: changeFilter,
    onPageChange: (_, value) => setPage(value),
    onSearch: (value) => {
      setSearch(value);
      setPage(1);
    },
    onSort: tableSort.toggleSort,
    onView: openDocument,
    page,
    rowsPerPage,
    onRowsPerPageChange: handleRowsPerPageChange,
    projects: scope.projects,
    resetSort: tableSort.resetSort,
    rows: rows.slice((page - 1) * rowsPerPage, page * rowsPerPage),
    search,
    sort: tableSort.sort,
    totalCount: rows.length,
    drawerProps: {
      form,
      projects: scope.projects,
      warehouses,
      sources: sources.data || [],
      articles: scope.articles,
      searchArticle: scope.setSearch,
      sourceId,
      legacyArticleId,
      onSource: setSourceId,
      onArticle: setLegacyArticleId,
      onChange: change,
      onAdd: add,
      onQuantity: changeQuantity,
      onRemove: (key) => change('items', form.items.filter((row) => row.key !== key)),
      onClose: () => {
        if (!mutation.isPending) setForm(null);
      },
      onSave: save,
      isSaving: mutation.isPending,
      error,
      readOnly: Boolean(form?.uuid) && form.status !== RETURN_STATUSES.PENDING,
      canSave: canSaveReturnForm(form, canReceive),
    },
  };
};
